import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Camera, Loader2, X } from "lucide-react";
import { api, getApiErrorMessage } from "../../api/client";
import { putToPresignedUrl } from "../../api/upload";
import { compressImage } from "../../lib/offline/image-compress";
import { unwrapArray } from "../../lib/api-envelope";
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from "../../lib/load-failure";
import { LoadFailureNotice } from "../ui/LoadFailureNotice";

/**
 * Rental inspection photos are confirmed R2 uploads filed against the
 * inspection's own id (api#1066). The order is fixed by the API:
 *
 *   1. the client mints the inspection id (`newInspectionId`), before the
 *      inspection exists;
 *   2. each photo: presign `rental_inspection` + that id → PUT → confirm;
 *   3. the inspection is filed with `id` and the keys in `photo_urls` /
 *      `damage_photos`.
 *
 * A key the API cannot find confirmed against that id is a 400 and nothing is
 * written, so a photo still uploading must hold the submit back: the field
 * reports `busy` and its screen disables Submit until it clears.
 *
 * ONLINE ONLY, deliberately. The shared PhotoUploader queues photos in
 * IndexedDB when offline, and a queued photo is not confirmed — the inspection
 * it was meant for would be refused. Here an upload is attempted and its
 * failure is shown, the same rule the Delivery and Return screens already
 * follow for their own save.
 *
 * Removing a photo takes it off the inspection; the upload itself stays under
 * the id (no route deletes it before the inspection exists), which is why the
 * display below lists only the keys the inspection actually names.
 */

export const RENTAL_INSPECTION_UPLOAD_TYPE = "rental_inspection";

/**
 * A v4 UUID. `crypto.randomUUID` where the runtime has it (every supported
 * browser), else built from random bytes. Not the `uuid` package: its
 * current major is ESM-only, so every screen test importing this file would
 * need a mock for it.
 */
export function newInspectionId(): string {
  const c: Crypto | undefined = globalThis.crypto;
  if (typeof c?.randomUUID === "function") return c.randomUUID();
  // An id, not a secret: with no Web Crypto at all (the jsdom test runtime)
  // any well-formed random v4 will do; the API refuses one already taken.
  const b = typeof c?.getRandomValues === "function"
    ? c.getRandomValues(new Uint8Array(16))
    : Uint8Array.from({ length: 16 }, () => Math.floor(Math.random() * 256));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

interface Shot {
  /** The R2 key once confirmed; null while it is uploading or after it failed. */
  key: string | null;
  preview: string;
  name: string;
  error: string | null;
}

const toJpegName = (name: string) => `${name.replace(/\.[^.]+$/, "") || "photo"}.jpg`;

async function uploadOne(inspectionId: string, file: File): Promise<string> {
  // Field photos are compressed as everywhere else; a picture the canvas
  // cannot read goes up as it is.
  let body: Blob = file;
  let contentType = file.type || "application/octet-stream";
  let fileName = file.name || "photo";
  try {
    body = await compressImage(file);
    contentType = "image/jpeg";
    fileName = toJpegName(fileName);
  } catch {
    /* send the original */
  }
  const presigned = (
    await api.uploads.getPresignedUrl({ fileName, contentType, entityType: RENTAL_INSPECTION_UPLOAD_TYPE, entityId: inspectionId })
  ).data as { uploadUrl?: string; key?: string };
  if (!presigned?.uploadUrl || !presigned.key) throw new Error("The upload could not be started.");
  await putToPresignedUrl(presigned.uploadUrl, body, contentType);
  await api.uploads.confirm({ key: presigned.key, size: body.size });
  return presigned.key;
}

/**
 * The photo field for one inspection. `onChange` receives the confirmed keys
 * only; `onBusyChange` is true while any photo is still uploading.
 */
export function RentalInspectionPhotoField({
  inspectionId,
  label = "Photos",
  idPrefix,
  onChange,
  onBusyChange,
  disabled,
}: {
  inspectionId: string;
  label?: string;
  idPrefix: string;
  onChange: (keys: string[]) => void;
  onBusyChange?: (busy: boolean) => void;
  disabled?: boolean;
}) {
  const [shots, setShots] = useState<Shot[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const busy = shots.some((s) => s.key === null && s.error === null);
  const keys = shots.map((s) => s.key).filter((k): k is string => !!k);
  const keyList = keys.join("|");

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onBusyRef = useRef(onBusyChange);
  onBusyRef.current = onBusyChange;
  useEffect(() => {
    onChangeRef.current(keyList ? keyList.split("|") : []);
  }, [keyList]);
  useEffect(() => {
    onBusyRef.current?.(busy);
  }, [busy]);

  // Each preview is an object URL; release them when the field goes away.
  const previews = useRef<string[]>([]);
  useEffect(() => () => previews.current.forEach((u) => URL.revokeObjectURL(u)), []);

  const add = (files: FileList | null) => {
    for (const file of Array.from(files ?? [])) {
      const preview = URL.createObjectURL(file);
      previews.current.push(preview);
      setShots((prev) => [...prev, { key: null, preview, name: file.name, error: null }]);
      uploadOne(inspectionId, file).then(
        (key) => setShots((prev) => prev.map((s) => (s.preview === preview ? { ...s, key } : s))),
        (err) =>
          setShots((prev) =>
            prev.map((s) => (s.preview === preview ? { ...s, error: getApiErrorMessage(err, err instanceof Error ? err.message : "The photo was not uploaded.") } : s)),
          ),
      );
    }
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = (preview: string) => setShots((prev) => prev.filter((s) => s.preview !== preview));

  return (
    <div className="space-y-2" data-testid={`${idPrefix}-photos`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-bold uppercase tracking-wider text-fieldiq-muted">{label}</span>
        <label
          htmlFor={`${idPrefix}-photo-input`}
          className={`inline-flex min-h-[40px] cursor-pointer items-center gap-2 rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 text-sm font-semibold text-fieldiq-text ${disabled ? "pointer-events-none opacity-50" : ""}`}
        >
          <Camera className="size-4" aria-hidden="true" />
          Add photo
        </label>
        <input
          ref={inputRef}
          id={`${idPrefix}-photo-input`}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          disabled={disabled}
          aria-label={`Add ${label.toLowerCase()}`}
          className="sr-only"
          onChange={(e) => add(e.target.files)}
        />
      </div>
      {shots.length > 0 && (
        <ul className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {shots.map((s) => (
            <li key={s.preview} data-testid="inspection-photo" className="relative overflow-hidden rounded-fi-control border border-fieldiq-border bg-fieldiq-dark">
              <img src={s.preview} alt={s.name} className="aspect-square w-full object-cover" />
              {s.key === null && s.error === null && (
                <span className="absolute inset-0 flex items-center justify-center bg-black/40" role="status" aria-label={`Uploading ${s.name}`}>
                  <Loader2 className="size-5 animate-spin text-white" aria-hidden="true" />
                </span>
              )}
              <button
                type="button"
                onClick={() => remove(s.preview)}
                aria-label={`Remove ${s.name}`}
                className="absolute right-1 top-1 flex size-7 items-center justify-center rounded-full bg-black/60 text-white"
              >
                <X className="size-4" aria-hidden="true" />
              </button>
              {s.error && (
                <p role="alert" className="border-t border-fieldiq-border px-1.5 py-1 text-[11px] leading-tight text-fieldiq-text">
                  Not uploaded: {s.error}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
      {busy && <p className="text-xs text-fieldiq-muted">Waiting for photos to finish uploading.</p>}
      {shots.some((s) => s.error) && <p className="text-xs text-fieldiq-muted">Remove a photo that did not upload, or add it again.</p>}
    </div>
  );
}

interface UploadRow {
  id?: string;
  s3Key?: string | null;
  key?: string | null;
  url?: string | null;
  fileName?: string | null;
}

/**
 * An inspection's photos, from GET /uploads/rental_inspection/:id (signed
 * URLs). `keys` is what the record names:
 *
 *   - `[]`        the record names no photo: "No photos", and nothing is fetched;
 *   - `[keys]`    only those uploads are shown (an upload removed from the form
 *                 before submit is still filed under the id);
 *   - `undefined` the record carries no photo list at all, so nothing can be
 *                 filtered and every upload under the id is shown.
 *
 * `[]` and `undefined` are opposite instructions; never `?? []` one into the other.
 */
export function RentalInspectionPhotoList({ inspectionId, keys }: { inspectionId: string; keys: string[] | undefined }) {
  const named = keys === undefined ? null : new Set(keys);
  const query = useQuery({
    queryKey: ["uploads", RENTAL_INSPECTION_UPLOAD_TYPE, inspectionId],
    queryFn: () => api.uploads.listForEntity(RENTAL_INSPECTION_UPLOAD_TYPE, inspectionId).then((r) => unwrapArray<UploadRow>(r.data)),
    enabled: !!inspectionId && (named === null || named.size > 0),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });

  if (named !== null && named.size === 0) return <p className="text-sm text-fieldiq-muted" data-testid="inspection-photos-none">No photos.</p>;
  if (queryLoadFailure(query) != null) return <LoadFailureNotice error={queryFailureError(query)} subject="photos" />;
  if (!query.data) return <Loader2 className="size-4 animate-spin text-fieldiq-muted" aria-label="Loading photos" />;

  const keyOf = (u: UploadRow) => u.s3Key ?? u.key ?? null;
  const shown = query.data.filter((u) => u.url && (named === null || (keyOf(u) !== null && named.has(keyOf(u) as string))));
  // A reference that matches no upload (an inspection from before api#1066
  // stored any string) is counted, not drawn.
  const unmatched = named === null ? 0 : Array.from(named).filter((k) => !query.data.some((u) => keyOf(u) === k)).length;

  return (
    <div className="space-y-2" data-testid="inspection-photo-list">
      {shown.length > 0 ? (
        <ul className="grid grid-cols-3 gap-2 sm:grid-cols-6">
          {shown.map((u, i) => (
            <li key={keyOf(u) ?? u.id ?? i}>
              <a href={u.url as string} target="_blank" rel="noreferrer" className="block overflow-hidden rounded-fi-control border border-fieldiq-border">
                <img src={u.url as string} alt={`Inspection shot ${i + 1} of ${shown.length}`} className="aspect-square w-full object-cover" />
              </a>
            </li>
          ))}
        </ul>
      ) : (
        unmatched === 0 && <p className="text-sm text-fieldiq-muted" data-testid="inspection-photos-none">No photos.</p>
      )}
      {unmatched > 0 && (
        <p className="text-xs text-fieldiq-muted" data-testid="inspection-photos-unmatched">
          {unmatched === 1 ? "1 photo reference is" : `${unmatched} photo references are`} not an upload and cannot be shown.
        </p>
      )}
    </div>
  );
}
